/** @jsxImportSource @emotion/react */
import {
  Drawer as MuiDrawer,
  List as MuiList,
  ListItemButton as MuiListItemButton,
  ListItemText as MuiListItemText,
} from '@mui/material';
import { Divider, HighlightTag, Tooltip } from 'components';
import { useMerchantData, useNavigation } from 'contexts';
import { FeatureFlag, PageRoute, PermissionEnum } from 'enums';
import { theme } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import SpotlightModule from 'modules/spotlight';
import {
  confirmSpotlightPopup,
  hadSpotlightPopup,
} from 'modules/spotlight/SpotlightUtil';
import productAndOffers from 'public/new-feature/Product_and_Offers.svg';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { statusFormatter } from 'utils';
import DrawerExpandableItem from '../drawer-expandable-item';
import MenuListItem, { InnerMenuItem, MenuItem } from '../menu-list-item';
import * as styles from './Drawer.styles';
import { DrawerProps } from './DrawerProps';

const Drawer: FC<DrawerProps> = ({
  isDrawerOpen,
  isInnerDrawerOpen,
  canShowSpotlight,
  setIsDrawerOpen,
  setIsInnerDrawerOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { menuItems, selectedItems } = useNavigation();
  const { checkPermission, currentUserId } = useMerchantData();
  const spotlightRef = useRef<HTMLDivElement>(null);
  const [shouldShowSpotlight, setShouldShowSpotlight] =
    useState<boolean>(false);

  const expiresSoonEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableExpiresSoon
  );
  const productConfigEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableProductConfig
  );
  const showProductConfig =
    productConfigEnabled &&
    checkPermission(PermissionEnum.ProductConfigExecute);

  function isNewFeature(item: string): boolean {
    return shouldShowSpotlight && item === 'product-config';
  }

  useEffect(() => {
    if (hadSpotlightPopup(currentUserId)) {
      return;
    }

    const shouldShowSpotlightValue = canShowSpotlight && showProductConfig;
    if (shouldShowSpotlightValue) {
      setShouldShowSpotlight(shouldShowSpotlightValue);
      confirmSpotlightPopup(currentUserId);
    }
  }, [showProductConfig]);

  function toggleDrawers(
    openDrawer: boolean,
    openInnerDrawer: boolean,
    override?: boolean
  ): void {
    setIsDrawerOpen(
      window.innerWidth > theme.breaks.md + 1 || override ? openDrawer : false
    );
    setIsInnerDrawerOpen(
      window.innerWidth > theme.breaks.md + 1 || override
        ? openInnerDrawer
        : false
    );
  }

  function handleInnerMenuItemClick(
    menuItem: InnerMenuItem,
    overrideSettings = false,
    changeRoute = true
  ): void {
    toggleDrawers(false, true, overrideSettings);
    if (location.pathname !== menuItem?.path && changeRoute) {
      navigate(menuItem?.path);
    }
    if (menuItem?.onClick) {
      menuItem?.onClick();
    }
  }

  function handleMenuItemClick(menuItem, changeRoute = true): void {
    toggleDrawers(menuItem?.openDrawer, menuItem?.openInnerDrawer);
    if (location.pathname !== menuItem?.path && changeRoute) {
      navigate(menuItem?.path);
    }
  }

  useEffect(() => {
    if (!selectedItems?.menuItem) {
      return;
    }
    const { menuItem } = selectedItems;
    toggleDrawers(menuItem.openDrawer, menuItem.openInnerDrawer);
  }, [selectedItems]);

  return (
    <>
      <MuiDrawer
        id="mainDrawer"
        variant="permanent"
        css={styles.drawer(isDrawerOpen)}
      >
        <div css={styles.flexList}>
          <MuiList css={styles.list}>
            {menuItems &&
              menuItems?.length > 0 &&
              menuItems
                ?.filter(
                  (x) => showProductConfig || x.path !== PageRoute.ProductConfig
                )
                ?.map((item: MenuItem) => {
                  const itemKey = `drawer-menu-item-${item.value}`;
                  return (
                    <React.Fragment key={itemKey}>
                      {isNewFeature(item.value) && (
                        <div
                          css={styles.overlay}
                          onClick={(): void => setShouldShowSpotlight(false)}
                        ></div>
                      )}
                      <Tooltip
                        content={!isDrawerOpen ? item.text : ''}
                        placement="right"
                        dense
                      >
                        <div
                          ref={isNewFeature(item.value) ? spotlightRef : null}
                          style={{ width: '100%' }}
                        >
                          <MenuListItem
                            item={item}
                            onClick={(): void => {
                              if (item?.onClick) {
                                item?.onClick();
                              }
                              handleMenuItemClick(item);
                            }}
                            selected={
                              selectedItems?.menuItem?.value === item.value
                            }
                            isDrawerOpen={isDrawerOpen}
                            css={[
                              isNewFeature(item.value) ? styles.spotlight : '',
                              shouldShowSpotlight ? styles.nonSpotlight : '',
                            ]}
                            children={
                              isNewFeature(item.value) && (
                                <SpotlightModule
                                  imageUrl={productAndOffers}
                                  targetElementRef={spotlightRef}
                                  text="Learn more about your interest free period setup, promotions and optimise offers."
                                  title="New feature"
                                  route={PageRoute.ProductConfig}
                                  setShouldShowSpotlight={
                                    setShouldShowSpotlight
                                  }
                                />
                              )
                            }
                          />
                        </div>
                      </Tooltip>
                    </React.Fragment>
                  );
                })}
          </MuiList>
        </div>
      </MuiDrawer>
      <MuiDrawer
        id="innerDrawer"
        variant="permanent"
        css={styles.innerDrawer(isInnerDrawerOpen)}
      >
        <p
          className="body1"
          style={{
            margin: 0,
            padding: '32px 24px 8px',
            borderBottom: '1px solid lightgrey',
          }}
        >
          {selectedItems?.menuItem?.text}
        </p>
        <div css={styles.flexList}>
          <MuiList css={styles.list}>
            {selectedItems?.menuItem.children
              ?.filter(
                (x) => expiresSoonEnabled || x.path !== PageRoute.ExpiringOrders
              )
              ?.map((item, i) => {
                const itemId = `menu-item-${selectedItems?.menuItem?.value}-${item.value}`;
                const itemKey = item.value || `item-${i}`;
                return (
                  <React.Fragment key={itemKey}>
                    {item?.divider === 'top' && <Divider top={0} />}
                    {item?.children && item?.children?.length > 0 ? (
                      <DrawerExpandableItem
                        id={itemId}
                        item={item}
                        innerSelectedItem={selectedItems?.innerMenuItem}
                        setInnerSelectedItem={(
                          child,
                          overrideDrawerClose
                        ): void =>
                          handleInnerMenuItemClick(child, overrideDrawerClose)
                        }
                        autoSelectOnOpen={item?.autoSelectOnOpen}
                      />
                    ) : (
                      <MuiListItemButton
                        id={itemId}
                        css={styles.listItem(
                          selectedItems?.innerMenuItem?.path === item.path
                        )}
                        onClick={(): void => handleInnerMenuItemClick(item)}
                        disableRipple
                      >
                        <MuiListItemText
                          primary={item.text || statusFormatter(item.value)}
                          css={styles.listItemText(true, !!item.tag)}
                        />
                        {item.tag && (
                          <HighlightTag
                            label={item.tag}
                            expiryDate={item.tagExpiry}
                          />
                        )}
                      </MuiListItemButton>
                    )}
                    {item?.divider === 'bottom' && <Divider />}
                  </React.Fragment>
                );
              })}
          </MuiList>
        </div>
      </MuiDrawer>
    </>
  );
};

export default Drawer;
